import { useEffect, useState } from 'react';

import { AdminSearch } from '../AdminSearch/AdminSearch';
import { CustomButton } from '../CustomButton/CustomButton';
import { Switch } from '../Switch /Switch';

import styles from './addProductModalTabs.module.css';

type AssignListModalProps<T, S> = {
    isOpen: boolean;
    title: string;
    data: T[];
    selectedItems: S[];
    search: string;
    setSearch: (value: string) => void;
    onClose: () => void;
    onSave: (items: S[]) => void;
    getId: (item: T) => string;
    isSelected: (item: T, selectedItems: S[]) => boolean;
    onToggle: (
        item: T,
        enabled: boolean,
        selectedItems: S[]
    ) => S[];
    renderInfo: (item: T) => React.ReactNode;
    renderSelectedContent?: (
        item: T,
        selectedItems: S[],
        setSelectedItems: React.Dispatch<React.SetStateAction<S[]>>
    ) => React.ReactNode;
    hasMore: boolean;
    onLoadMore?: () => void;
    isLoading?: boolean;
    selectedLabel?: (count: number) => string;
};

export const AssignListModal = <T, S>({
    isOpen,
    title,
    data,
    selectedItems,
    search,
    setSearch,
    onClose,
    onSave,
    getId,
    isSelected,
    onToggle,
    renderInfo,
    renderSelectedContent,
    hasMore,
    onLoadMore,
    isLoading = false,
    selectedLabel,
}: AssignListModalProps<T, S>) => {
    const [tempSelectedItems, setTempSelectedItems] =
        useState<S[]>([]);

    useEffect(() => {
        if (isOpen) {
            setTempSelectedItems(selectedItems);
        }
    }, [isOpen, selectedItems]);

    const handleToggle = (
        item: T,
        enabled: boolean
    ) => {
        setTempSelectedItems((prev) =>
            onToggle(item, enabled, prev)
        );
    };

    const handleSave = () => {
        onSave(tempSelectedItems);
        onClose();
    };

    if (!isOpen) return null;
    return <div className={styles.assignListModalOverlay} onClick={onClose}>
        <div className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}>
            <p className={styles.modalTitle}>{title}</p>
            <button className={styles.closeButton} onClick={onClose}>
                &times;
            </button>

            <div className={styles.modalBody}>
                <AdminSearch searchTerm={search} handleInputChange={(e) => setSearch(e.target.value)} width='100%' height='40px' />
                <div className={styles.modalList}>
                    {data.map((item) => {
                        const id = getId(item);
                        const enabled = isSelected(
                            item,
                            tempSelectedItems
                        );

                        return (
                            <div
                                key={id}
                                className={`${styles.modalListElement} ${enabled ? styles.selected : ''}`}
                            >
                                <Switch
                                    checked={enabled}
                                    onChange={(value) => handleToggle(
                                        item,
                                        value
                                    )}
                                />

                                <div className={styles.modalListElementInfo}>
                                    {renderInfo(item)}

                                    {renderSelectedContent &&
                                        enabled &&
                                        renderSelectedContent(
                                            item,
                                            tempSelectedItems,
                                            setTempSelectedItems
                                        )}
                                </div>
                            </div>
                        );
                    })}
                    {hasMore && (
                        <button
                            type="button"
                            onClick={onLoadMore}
                            disabled={isLoading}
                            className={styles.loadMoreButton}
                        >
                            {isLoading
                                ? "Laden..."
                                : "Mehr laden"}
                        </button>
                    )}
                </div>
            </div>
            <div className={styles.modalFooter}>
                {selectedLabel && tempSelectedItems.length > 0 && (
                    <p className={styles.modalFooterCount}>{selectedLabel(tempSelectedItems.length)}</p>
                )}
                <CustomButton color='outlined' customClass={styles.modalButton} onClick={onClose}>Abbrechen</CustomButton>
                <CustomButton color='desktopGreen' customClass={styles.modalButton} onClick={handleSave}>Speichern</CustomButton>
            </div>
        </div>
    </div>;
};